import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, formatDate, categorySlug } from "@/lib/api";
import { ArticleCard, ArticleRow } from "@/components/ArticleCard";
import { resizeImage } from "@/lib/img";
import { HomeMarketsModule } from "@/components/HomeMarketsModule";
import MidtermsHub from "@/components/MidtermsHub";
import { SeasonalCover, MonthlyCover } from "@/components/HomeCovers";
import { InConversationWith } from "@/components/InConversationWith";
import Seo, { absoluteUrl, SITE_NAME } from "@/components/Seo";
import AdSlot from "@/components/AdSlot";

function FrontPageHero({ lead, secondary = [], strip = [] }) {
  if (!lead) return null;
  return (
    <section className="px-4 sm:px-6 lg:px-10 pt-8 md:pt-12 pb-10 md:pb-14 border-b border-black/15" data-testid="front-page-hero">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
        {/* Lead — massive */}
        <Link to={`/article/${lead.slug}`} className="article-card lg:col-span-8 block group" data-testid={`lead-${lead.slug}`}>
          <div className="zoom-img aspect-[16/10] bg-neutral-100 mb-5 md:mb-7 relative">
            <img src={resizeImage(lead.cover_image, "hero")} alt={lead.title} fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3">
              <span className="bg-[var(--hinton-red)] text-white text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1">{lead.category}</span>
            </div>
          </div>
          <div className="overline text-neutral-500 mb-3">{lead.category} · {formatDate(lead.created_at)}</div>
          <h1 className="article-title font-serif-ed font-semibold text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight transition-colors">
            {lead.title}
          </h1>
          {lead.excerpt && (
            <p className="mt-5 text-neutral-700 text-base md:text-lg leading-relaxed max-w-3xl">
              {lead.excerpt}
            </p>
          )}
          {lead.author && lead.author !== "Hinton Editorial" && (
            <div className="mt-4 text-[11px] font-mono-ed uppercase tracking-[0.22em] text-neutral-500">
              By {lead.author}
            </div>
          )}
        </Link>

        {/* Right column — two stacked + a divider */}
        <div className="lg:col-span-4 flex flex-col divide-y divide-black/15">
          {secondary.slice(0, 2).map((a, idx) => (
            <Link key={a.id} to={`/article/${a.slug}`} className="article-card block py-5 first:pt-0 group" data-testid={`hero-secondary-${a.slug}`}>
              <div className="zoom-img aspect-[16/10] bg-neutral-100 mb-4">
                <img src={resizeImage(a.cover_image, "card")} alt={a.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="overline text-neutral-500 mb-2">{a.category} · {formatDate(a.created_at)}</div>
              <h3 className="article-title font-serif-ed font-semibold text-xl md:text-2xl leading-tight tracking-tight transition-colors">
                {a.title}
              </h3>
            </Link>
          ))}
        </div>
      </div>

      {/* Latest strip */}
      {strip.length > 0 && (
        <div className="mt-10 md:mt-14 pt-6 border-t border-black/15">
          <div className="flex items-center justify-between mb-5">
            <div className="overline text-[var(--hinton-red)]">Latest</div>
            <Link to="/search" className="text-[11px] uppercase tracking-[0.22em] font-semibold hover:text-[var(--hinton-red)] transition-colors">All latest</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {strip.map((a) => (
              <Link key={a.id} to={`/article/${a.slug}`} className="article-card block group" data-testid={`hero-strip-${a.slug}`}>
                <div className="zoom-img aspect-[4/3] bg-neutral-100 mb-3">
                  <img src={resizeImage(a.cover_image, "card")} alt={a.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="overline text-neutral-500 mb-1.5">{a.category}</div>
                <h4 className="article-title font-serif-ed font-semibold text-base md:text-lg leading-snug tracking-tight transition-colors line-clamp-3">
                  {a.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MarqueeStrip({ items }) {
  if (!items?.length) return null;
  const loop = [...items, ...items];
  return (
    <div className="ticker-strip overflow-hidden border-y border-white/10" data-testid="ticker-strip">
      <div className="marquee-track py-3">
        {loop.map((t, i) => (
          <span key={`${t}-${i}`} className="px-8 text-xs uppercase tracking-[0.3em] font-mono-ed text-neutral-200 whitespace-nowrap">
            {t} <span className="text-[var(--hinton-red)] ml-8">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ kicker, title, link, linkText = "All stories" }) {
  return (
    <div className="flex items-end justify-between border-b border-black/20 pb-4 mb-8 md:mb-10">
      <div>
        {kicker && <div className="overline text-[var(--hinton-red)] mb-2">{kicker}</div>}
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.03em]">{title}</h2>
      </div>
      {link && (
        <Link to={link} className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold hover:text-[var(--hinton-red)] transition-colors" data-testid={`section-link-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          {linkText} <ArrowUpRight size={14} />
        </Link>
      )}
    </div>
  );
}

function DrinkCard({ drink }) {
  const rows = [
    ["Taste", drink.taste],
    ["Label Design", drink.label_design],
    ["Value", drink.value],
    ["Overall", drink.overall],
  ];
  return (
    <div className="border border-black/15 bg-white p-5 flex flex-col h-full" data-testid={`drink-card-${drink.id}`}>
      {drink.image_url && (
        <div className="aspect-[3/4] bg-[#F4F1EA] mb-4 overflow-hidden flex items-center justify-center">
          <img src={drink.image_url} alt={drink.name} loading="lazy" className="w-full h-full object-contain p-2" />
        </div>
      )}
      <div className="overline text-[var(--hinton-red)] mb-2">{drink.category}</div>
      <h3 className="font-serif-ed font-semibold text-xl leading-tight">{drink.name}</h3>
      <div className="mt-1 text-sm font-mono-ed text-neutral-600">{drink.cost}</div>
      <div className="mt-4 space-y-2.5">
        {rows.map(([label, val]) => (
          <div key={label}>
            <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] font-semibold mb-1">
              <span>{label}</span>
              <span className="font-mono-ed text-neutral-600">{val.toFixed(1)}/5</span>
            </div>
            <div className="rating-bar-track"><div className="rating-bar-fill" style={{ width: `${(val/5)*100}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-neutral-700 leading-relaxed line-clamp-3">{drink.review}</p>
    </div>
  );
}


// Static JSON-LD for the homepage — hoisted to module scope so it doesn't
// Homepage-specific JSON-LD bundle (Organization + WebSite + SearchAction).
// Defined once outside the component so the reference is stable and doesn't
// trigger a re-render of <Seo> on every Home render.
const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "NewsMediaOrganization",
      "@id": "https://hintononline.com/#organization",
      "name": SITE_NAME,
      "legalName": "Hinton.",
      "alternateName": ["Hinton Online", "Hinton Magazine"],
      "url": "https://hintononline.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://hintononline.com/logo512.png",
        "width": 512,
        "height": 512,
      },
      "image": "https://hintononline.com/og-default.jpg",
      "description": "Hinton is a British editorial publication founded in 2017, covering business, markets, politics, culture, fashion, technology and lifestyle with serious reporting and sharp cultural commentary.",
      "foundingDate": "2017",
      "areaServed": "GB",
      "sameAs": [
        "https://www.instagram.com/hintononline/",
        "https://www.facebook.com/HintonOnline",
        "https://x.com/Hinton_Online",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://hintononline.com/#website",
      "name": SITE_NAME,
      "alternateName": ["Hinton Online", "Hinton Magazine"],
      "url": "https://hintononline.com",
      "publisher": { "@id": "https://hintononline.com/#organization" },
      "inLanguage": "en-GB",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${absoluteUrl("/search")}?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ],
};

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [perCat, setPerCat] = useState({});
  const [drinks, setDrinks] = useState([]);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Kick off every request in parallel, but do NOT block the whole
    // page on Promise.all. As soon as the main feed returns we drop
    // the loading placeholder; per-category rails, drinks and the
    // admin layout override then fill in progressively as they
    // resolve, so a single slow endpoint can never hold up the paint.
    let alive = true;

    api.get("/articles?limit=80")
      .then((r) => { if (alive) setArticles(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });

    api.get("/drinks")
      .then((r) => { if (alive) setDrinks(r.data); })
      .catch(() => {});

    api.get("/home/layout")
      .then((r) => { if (alive) setLayout(r.data); })
      .catch(() => {});

    const cats = ["fashion", "watches", "culture", "hinton-visits", "technology", "lifestyle", "business", "markets", "in-conversation-with", "health", "news", "ed-fringe-2026"];
    cats.forEach((slug) => {
      api.get(`/articles?category=${slug}&limit=12`)
        .then((r) => {
          if (!alive) return;
          setPerCat((prev) => ({ ...prev, [slug]: r.data }));
        })
        .catch(() => {});
    });

    return () => { alive = false; };
  }, []);

  const byCategory = (cat) => articles.filter((a) => a.category === cat);
  // Covers render in their own block; exclude them from the front-page hero rows.
  const nonCover = articles.filter((a) => !a.is_cover);
  // Lead is the latest featured non-cover story; fallback to latest non-cover overall.
  const featuredLatest = nonCover.filter((a) => a.featured);
  const autoLead = featuredLatest[0] || nonCover[0];
  // Manual layout from admin (if set) overrides the automatic pick.
  const manualLead = layout?.lead;
  const manualSecondary = layout?.secondary || [];
  const manualStrip = layout?.strip || [];
  const lead = manualLead || autoLead;
  const autoSecondary = nonCover
    .filter((a) => a.id !== lead?.id)
    .slice(0, 2);
  // For each of the 2 secondary slots, use the admin pick if present, else fall back to auto.
  const secondary = (() => {
    const out = [];
    for (let i = 0; i < 2; i++) {
      const manual = manualSecondary[i];
      if (manual && manual.id !== lead?.id) {
        out.push(manual);
      } else {
        // fill from autoSecondary, skipping anything already in `out`
        const next = autoSecondary.find((a) => !out.some((o) => o.id === a.id) && a.id !== lead?.id);
        if (next) out.push(next);
      }
    }
    return out;
  })();
  const autoStrip = nonCover
    .filter((a) => a.id !== lead?.id && !secondary.some((s) => s.id === a.id))
    .slice(0, 4);
  const strip = (() => {
    const out = [];
    for (let i = 0; i < 4; i++) {
      const manual = manualStrip[i];
      if (
        manual &&
        manual.id !== lead?.id &&
        !secondary.some((s) => s.id === manual.id) &&
        !out.some((o) => o.id === manual.id)
      ) {
        out.push(manual);
      } else {
        const next = autoStrip.find(
          (a) =>
            !out.some((o) => o.id === a.id) &&
            !secondary.some((s) => s.id === a.id) &&
            a.id !== lead?.id
        );
        if (next) out.push(next);
      }
    }
    return out;
  })();

  // Each rail prefers per-category data, then falls back to the global feed.
  const fashion = (perCat["fashion"] || byCategory("Fashion")).slice(0, 5);
  const watches = (perCat["watches"] || byCategory("Watches")).slice(0, 4);
  const culture = (perCat["culture"] || byCategory("Culture")).slice(0, 5);
  const visits  = (perCat["hinton-visits"] || byCategory("Hinton Visits")).slice(0, 5);
  const interviews = (perCat["lifestyle"] || []).slice(0, 4);
  const tech = (perCat["technology"] || []).slice(0, 3);
  // Strictly business articles on the home page — the old code merged
  // "business-finance" with "markets" which let market-data pieces leak
  // into the Business Desk. Editorial brief: business-only here.
  const business = (perCat["business"] || []).slice(0, 5);
  const sport = (perCat["sport"] || []).slice(0, 3);
  // For mid-page editorial spotlight, surface any active cover-story article.
  const editorialSpotlight = articles.find((a) => a.is_cover && a.id !== lead?.id);

  if (loading) {
    return <div className="px-6 py-32 text-center font-serif-ed italic text-2xl text-neutral-500">Pressing the issue…</div>;
  }

  // Empty state — no articles yet (Newsroom is fresh / clean)
  if (articles.length === 0) {
    return (
      <div data-testid="home-empty">
        <section className="px-4 sm:px-6 lg:px-10 py-24 md:py-32 max-w-5xl">
          <div className="overline text-[var(--hinton-red)] mb-4">The Newsroom</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-[-0.045em] leading-[0.9]">
            The press is warming up.
          </h1>
          <p className="mt-6 max-w-2xl text-neutral-700 text-lg md:text-xl leading-relaxed font-serif-ed italic">
            Hinton&rsquo;s editors are preparing the next set of stories. Check back shortly, or sign in to the Newsroom to begin publishing.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/markets" className="bg-black text-white px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[var(--hinton-red)] transition-colors inline-flex items-center gap-2" data-testid="empty-cta-markets">
              Live markets <ArrowUpRight size={14} />
            </Link>
            <Link to="/hinton-bar" className="border border-black px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2" data-testid="empty-cta-bar">
              Hinton Bar <ArrowUpRight size={14} />
            </Link>
            <Link to="/login" className="px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold text-neutral-700 hover:text-[var(--hinton-red)] transition-colors inline-flex items-center gap-2" data-testid="empty-cta-newsroom">
              Newsroom sign in <ArrowUpRight size={14} />
            </Link>
          </div>
        </section>
        <HomeMarketsModule />
      </div>
    );
  }

  const tickerItems = [
    "Hinton.", "Live Markets", "Hinton Bar", "In Conversation With…", "Hinton Visits",
  ];

  return (
    <div data-testid="home-page">
      <Seo
        title="Your Source for Politics, Business, Culture and More"
        description="Hinton. Your source for politics, business, culture and more, serious reporting and sharp cultural commentary from Britain."
        canonical="/"
        image="/og-default.jpg"
        jsonLd={HOME_JSON_LD}
      />
      <FrontPageHero lead={lead} secondary={secondary} strip={strip} />
      <MarqueeStrip items={tickerItems} />

      {/* MIDTERMS '26 — live state-by-state results, above Fashion */}
      <MidtermsHub />

      {/* Homepage TOP banner ad — leaderboard between hero strip and the first
          editorial rail. Renders nothing until AD_SLOTS.homeTop.slot is set. */}
      <div className="px-4 sm:px-6 lg:px-10 pt-8">
        <div className="max-w-5xl mx-auto">
          <AdSlot id="homeTop" />
        </div>
      </div>

      {/* Fashion */}
      {fashion.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 bg-white border-y border-black/10">
          <SectionHeader kicker="Style" title="Fashion" link="/category/fashion" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {fashion[0] && (
              <div className="md:col-span-7">
                <ArticleCard article={fashion[0]} size="lg" />
              </div>
            )}
            <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6 content-start">
              {fashion.slice(1, 5).map((a) => (
                <ArticleCard key={a.id} article={a} size="sm" showExcerpt={false} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Technology */}
      {tech.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20">
          <SectionHeader kicker="The Briefing" title="Technology" link="/category/technology" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {tech.slice(0, 3).map((a) => (
              <ArticleCard key={a.id} article={a} size="md" />
            ))}
          </div>
        </section>
      )}

      {/* LIVE MARKETS MODULE — Bloomberg feel */}
      <HomeMarketsModule />

      {/* Business briefings */}
      {business.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 bg-white border-y border-black/10">
          <SectionHeader kicker="The Business Desk" title="Business" link="/news?tab=business" linkText="Read business" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {business[0] && (
              <div className="md:col-span-7">
                <ArticleCard article={business[0]} size="lg" />
              </div>
            )}
            <div className="md:col-span-5 flex flex-col">
              {business.slice(1, 5).map((a) => (
                <ArticleRow key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ed Fringe 2026 banner removed by request — stories now flow into the
          Culture section via the backend articles query alias. */}

      {/* In Conversation banner — removed: replaced by dedicated SeasonalCover & MonthlyCover blocks */}

      {/* SEASONAL COVER — full magazine cover, light theme */}
      <SeasonalCover />

      {/* Hinton Visits + Culture two-column */}
      {(visits.length > 0 || culture.length > 0) && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 bg-white border-y border-black/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {visits.length > 0 && (
              <div className="flex flex-col">
                <SectionHeader kicker="On Location" title="Hinton Visits" link="/category/hinton-visits" />
                <div className="flex flex-col flex-1">
                  {visits.slice(0, 5).map((a) => <ArticleRow key={a.id} article={a} />)}
                </div>
              </div>
            )}
            {culture.length > 0 && (
              <div className="flex flex-col">
                <SectionHeader kicker="In the World" title="Culture" link="/category/culture" />
                <div className="flex flex-col flex-1">
                  {culture.slice(0, 5).map((a) => <ArticleRow key={a.id} article={a} />)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Lifestyle grid */}
      {interviews.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20">
          <SectionHeader kicker="The Good Life" title="Lifestyle" link="/category/lifestyle" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {interviews.map((a) => (
              <ArticleCard key={a.id} article={a} size="sm" showExcerpt={false} />
            ))}
          </div>
        </section>
      )}

      {/* MONTHLY COVER — full magazine cover, dark theme */}
      <MonthlyCover />

      {/* In Conversation With… */}
      <InConversationWith />

      {/* Hinton Bar */}
      <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 bg-[#111] text-white">
        <div className="flex items-end justify-between border-b border-white/20 pb-4 mb-8 md:mb-10">
          <div>
            <div className="overline text-[var(--hinton-red)] mb-2">Tasting Notes</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.03em]">Hinton Bar</h2>
          </div>
          <Link to="/hinton-bar" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold hover:text-[var(--hinton-red)] transition-colors">
            Enter the bar <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {drinks.slice(0, 4).map((d) => (
            <div key={d.id} className="text-black">
              <DrinkCard drink={d} />
            </div>
          ))}
        </div>
      </section>

      {/* Homepage BOTTOM banner ad — last placement before the footer. */}
      <div className="px-4 sm:px-6 lg:px-10 pt-10">
        <div className="max-w-5xl mx-auto">
          <AdSlot id="homeBottom" />
        </div>
      </div>

      {/* Watches bottom rail */}
      {watches.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 border-t border-black/10">
          <SectionHeader kicker="Mechanical" title="Watches" link="/category/watches" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {watches.map((a) => (
              <ArticleCard key={a.id} article={a} size="sm" showExcerpt={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
